import { create } from 'zustand'
import { clearSession } from '../utils/sessionKeys'
import logger from '../utils/logger'

// Rejestracja jest zamknięta do momentu ukończenia migracji na Convex
const REGISTRATION_ENABLED = false

interface AuthUser {
  id: string
  email: string
}

interface AuthState {
  user: AuthUser | null
  loading: boolean
  initialized: boolean

  // Clerk sync — wywoływane z komponentu ClerkAuthSync
  setUser: (user: AuthUser | null) => void
  setLoading: (loading: boolean) => void
  setInitialized: (initialized: boolean) => void

  // Sign out — czyści lokalne dane (Clerk session jest zarządzany przez ClerkProvider)
  signOut: () => Promise<void>

  // Legacy — utrzymane dla kompatybilności, zawsze rzuca błąd jeśli wywołane bezpośrednio
  signUp: (email: string, password: string, marketingConsent: boolean) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>

  // Legacy — stub do kompatybilności z App.tsx watchdog
  initialize: () => Promise<void>

  isRegistrationEnabled: () => boolean
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  loading: true,
  initialized: false,

  setUser: (user) => {
    logger.debug('Auth: setUser', user?.email ?? 'null')
    set({ user })
  },

  setLoading: (loading) => set({ loading }),

  setInitialized: (initialized) => set({ initialized }),

  signUp: async () => {
    if (!REGISTRATION_ENABLED) {
      throw new Error('Registration is temporarily closed. It will reopen after migration is complete.')
    }
    // Sign-up jest teraz obsługiwany przez Clerk <SignUp /> komponent
    throw new Error('Use Clerk SignUp component instead')
  },

  signIn: async () => {
    // Sign-in jest teraz obsługiwany przez Clerk <SignIn /> lub useSignIn()
    throw new Error('Use Clerk SignIn component instead')
  },

  signOut: async () => {
    logger.debug('Starting local sign out cleanup...')
    try {
      // Wyczyść klucze E2EE z pamięci (device keypair zostaje w IndexedDB)
      clearSession()

      // Wyczyść sessionStorage
      sessionStorage.clear()

      // Wyczyść cache store'ów
      try {
        const { useUserPreferencesStore } = await import('./userPreferencesStore')
        useUserPreferencesStore.getState().clearCache()
      } catch (e) {
        logger.error('Error clearing preferences cache:', e)
      }

      try {
        const { useCalendarStore } = await import('./calendarStore')
        useCalendarStore.getState().clearCache()
      } catch (e) {
        logger.error('Error clearing calendar cache:', e)
      }

      try {
        const { useFamilyStore } = await import('./familyStore')
        useFamilyStore.setState({
          initialized: false,
          family: null,
          members: [],
          children: [],
          isLoading: false,
          error: null
        })
        localStorage.removeItem('2homes-family-storage')
      } catch (e) {
        logger.error('Error clearing familyStore:', e)
      }

      // Wyczyść stare klucze Supabase z localStorage (cleanup migracji)
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key))

      set({ user: null, loading: false, initialized: true })
      logger.debug('Local sign out cleanup complete')
    } catch (error) {
      logger.error('SignOut cleanup failed:', error)
      set({ user: null, loading: false, initialized: true })
      throw error
    }
  },

  initialize: async () => {
    // Inicjalizacja jest teraz automatyczna przez ClerkAuthSync komponent
    logger.debug('Auth initialize called (no-op, Clerk handles auth state)')
  },

  isRegistrationEnabled: () => REGISTRATION_ENABLED,
}))
